import { useEffect, useRef, useState } from 'react';

/**
 * `usePolledCounts` — the numbers behind a console's menu badges and bell (D22 NAV-COUNTS, D560), polled like the Admin's
 * alerts were: once on mount, then every `intervalMs` (60 s) **only while the tab is visible**, and again the moment it
 * becomes visible after being hidden. A hidden tab makes no request.
 *
 * It never invents a number: `read` resolves to the counts it really got, or to `undefined` when the read failed or was
 * refused — and then the hook answers `failed` with no values ("no badge"), never `0` and never the previous value (a stale number
 * on a badge is as wrong as an invented one). `resetKey` changes (the scope, the grants) drop the current numbers at once
 * and read again; a late answer to an older key is discarded.
 */
export const POLLED_COUNTS_INTERVAL_MS = 60_000;

export interface PolledCountsOptions {
  readonly intervalMs?: number;
  /** When false nothing is read (no session, no scope yet). */
  readonly enabled?: boolean;
  /** Changes whenever the numbers must be re-read from scratch (scope, grants, store). */
  readonly resetKey?: string;
}

export type PolledCountValues<K extends string, V = number> = Readonly<Partial<Record<K, V>>>;

/**
 * `loading`: no answer yet for this scope; `ready`: `values` holds what the last read really returned (a key the read did not
 * return has no number); `failed`: the last read failed or was refused, `values` is empty.
 */
export interface PolledCounts<K extends string, V = number> {
  readonly status: 'loading' | 'ready' | 'failed';
  readonly values: PolledCountValues<K, V>;
}

const NO_VALUES: PolledCountValues<never, never> = Object.freeze({});

export function usePolledCounts<K extends string, V = number>(
  read: () => Promise<PolledCountValues<K, V> | undefined>,
  { intervalMs = POLLED_COUNTS_INTERVAL_MS, enabled = true, resetKey = '' }: PolledCountsOptions = {},
): PolledCounts<K, V> {
  const [state, setState] = useState<PolledCounts<K, V>>({ status: 'loading', values: NO_VALUES });
  const latest = useRef(read);
  latest.current = read;

  useEffect(() => {
    setState({ status: 'loading', values: NO_VALUES });
    if (!enabled) return undefined;
    let cancelled = false;
    let seq = 0;
    const load = async (): Promise<void> => {
      const mine = ++seq;
      let next: PolledCountValues<K, V> | undefined;
      try {
        next = await latest.current();
      } catch {
        next = undefined;
      }
      if (cancelled || mine !== seq) return;
      setState(next === undefined ? { status: 'failed', values: NO_VALUES } : { status: 'ready', values: next });
    };
    const tick = (): void => {
      if (document.visibilityState === 'visible') void load();
    };
    tick();
    const timer = window.setInterval(tick, intervalMs);
    document.addEventListener('visibilitychange', tick);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [enabled, intervalMs, resetKey]);

  return state;
}

/** The largest number a badge draws; above it the badge says `99+`. */
export const BADGE_COUNT_CAP = 99;

/** The text of a count badge: the number, or `99+`; `atLeast` (a lower bound) adds a `+` — `3+`, never an exact-looking `3`. */
export function badgeCountText(count: number, atLeast = false): string {
  if (count > BADGE_COUNT_CAP) return `${BADGE_COUNT_CAP}+`;
  return atLeast ? `${count}+` : String(count);
}
